import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../firebase';
import { doc, query, collection, where, getDocs, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { updateProfile } from 'firebase/auth';
import { loadRoleConfig, resolveProfessionalRoles, resolveGradeLevels } from '../utils/roleConfig';
import { isCleverRosterDoc } from '../utils/rosterSource';
import { useLoadTimeout } from '../hooks/usePageLoad';

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const NOT_LISTED_VALUE = '__NOT_LISTED__';

// Prepend a stored profile value the configured option list doesn't contain,
// so the select can render (and re-select) it instead of the placeholder.
const withStoredOption = (options, stored) => (
  stored && !options.includes(stored) ? [stored, ...options] : options
);

function OnboardingModal({ user, onComplete, isEditMode = false, onClose = () => {} }) {
  const [formData, setFormData] = useState({
    displayName: user?.profile?.displayName || user?.displayName || '',
    state: user?.profile?.state || 'WI',
    districtId: user?.profile?.districtId || '',
    districtName: user?.profile?.district || '',
    schoolId: user?.profile?.schoolId || '',
    school: user?.profile?.school || '',
    grade: user?.profile?.grade || '',
    role: user?.profile?.role || ''
  });

  // 'dropdown' | 'freetext' — switches when user picks "not listed"
  const [districtMode, setDistrictMode] = useState(
    user?.profile?.districtId ? 'dropdown' : (user?.profile?.district ? 'freetext' : 'dropdown')
  );
  const [customDistrictName, setCustomDistrictName] = useState('');

  // Same pattern for school
  const [schoolMode, setSchoolMode] = useState(
    user?.profile?.schoolId ? 'dropdown' : (user?.profile?.school ? 'freetext' : 'dropdown')
  );
  const [customSchoolName, setCustomSchoolName] = useState('');

  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(true);

  const [availableSchools, setAvailableSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);

  // Role config (defaults). District-specific overrides come from the
  // selected district's `roleOverrides` field on the district doc, which
  // is already in `availableDistricts` after the load below.
  const [roleConfig, setRoleConfig] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load active districts + schools + role config defaults
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getDocs(query(collection(db, 'districts'), where('active', '==', true))),
      getDocs(query(collection(db, 'schools'),   where('active', '==', true))),
      loadRoleConfig(),
    ])
      .then(([dSnap, sSnap, cfg]) => {
        if (cancelled) return;
        setAvailableDistricts(
          dSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name))
        );
        setAvailableSchools(
          sSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
        setRoleConfig(cfg);
      })
      .catch(err => {
        console.error("Failed to load registries:", err);
        if (!cancelled) setError('We couldn\'t load the district and school lists. Please check your connection and reopen this page, or contact support.');
      })
      .finally(() => { if (!cancelled) { setLoadingDistricts(false); setLoadingSchools(false); } });
    return () => { cancelled = true; };
  }, []);

  // The registry fetch settles its own flags on success and on rejection, but
  // an offline Firestore getDocs can stay pending forever — which would leave
  // the dropdowns reading "Loading districts…" with no way out. Same 15s
  // backstop the pages get (2026-07 perf audit, step 8).
  const registriesTimedOut = useLoadTimeout(loadingDistricts || loadingSchools);
  const registryError = error || (registriesTimedOut
    ? "We couldn't load the district and school lists. Please check your connection and reopen this page, or contact support."
    : '');

  // Resolve dropdown options from defaults + the selected district's overrides.
  const selectedDistrict = useMemo(
    () => availableDistricts.find((d) => d.id === formData.districtId) || null,
    [availableDistricts, formData.districtId],
  );
  const districtOverrides = selectedDistrict?.roleOverrides || null;
  const professionalRoleOptions = useMemo(
    () => resolveProfessionalRoles(roleConfig, districtOverrides),
    [roleConfig, districtOverrides],
  );
  const gradeLevelOptions = useMemo(
    () => resolveGradeLevels(roleConfig, districtOverrides),
    [roleConfig, districtOverrides],
  );

  // Clever JIT stores values these configurable lists may not contain (the
  // Clever job title as role, the sections-derived grade — e.g. "8th Grade").
  // A select silently renders its placeholder for an unlisted value, and
  // saving in that state would wipe the stored field — so the profile's own
  // saved value is always offered as an option (pinned from the profile, not
  // the live selection, so switching away and back stays possible).
  const roleOptionsShown = useMemo(
    () => withStoredOption(professionalRoleOptions, user?.profile?.role),
    [professionalRoleOptions, user],
  );
  const gradeOptionsShown = useMemo(
    () => withStoredOption(gradeLevelOptions, user?.profile?.grade),
    [gradeLevelOptions, user],
  );

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDistrictSelect = (e) => {
    const val = e.target.value;
    if (val === NOT_LISTED_VALUE) {
      setDistrictMode('freetext');
      setFormData(prev => ({ ...prev, districtId: '', districtName: '', schoolId: '', school: '' }));
      setCustomDistrictName('');
    } else {
      const found = availableDistricts.find(d => d.id === val);
      // Reset school selection — old school may not belong to the new district
      setFormData(prev => ({
        ...prev,
        districtId: val,
        districtName: found?.name || '',
        ...(val !== prev.districtId ? { schoolId: '', school: '' } : {}),
      }));
      // If we were in school freetext mode, switching districts should reset us back to dropdown
      if (val !== formData.districtId) {
        setSchoolMode('dropdown');
        setCustomSchoolName('');
      }
    }
  };

  const handleSchoolSelect = (e) => {
    const val = e.target.value;
    if (val === NOT_LISTED_VALUE) {
      setSchoolMode('freetext');
      setFormData(prev => ({ ...prev, schoolId: '', school: '' }));
      setCustomSchoolName('');
    } else {
      const found = availableSchools.find(s => s.id === val);
      setFormData(prev => ({
        ...prev,
        schoolId: val,
        school: found?.name || '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const districtIsFreeText = districtMode === 'freetext';
    const districtNameFinal = districtIsFreeText ? customDistrictName.trim() : formData.districtName;
    const districtIdFinal = districtIsFreeText ? null : (formData.districtId || null);

    const schoolIsFreeText = schoolMode === 'freetext';
    const schoolNameFinal = schoolIsFreeText ? customSchoolName.trim() : (formData.school || '').trim();
    const schoolIdFinal = schoolIsFreeText ? null : (formData.schoolId || null);

    if (!formData.state || !districtNameFinal || !schoolNameFinal || !formData.grade || !formData.role) {
      setError('Please fill out all fields to continue.');
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedName = formData.displayName.trim();

      // The profile is saved by the saveOnboardingProfile callable, NOT a
      // direct setDoc: the users rules deliberately lock the tenant fields
      // (district/school/role) against client self-assignment, which made
      // every direct onboarding save fail with permission-denied (2026-07
      // audit, 3b#4). The callable validates district/school against the
      // registries server-side and writes the flag/notification docs too.
      const saveOnboardingProfile = httpsCallable(functions, 'saveOnboardingProfile');
      const result = await saveOnboardingProfile({
        // v3.84.8: the callable refuses to CREATE over an existing profile;
        // only the Profile Settings path may update one.
        intent: isEditMode ? 'update' : 'create',
        displayName: trimmedName || user.email,
        state: formData.state,
        district: districtNameFinal,
        districtId: districtIdFinal,
        school: schoolNameFinal,
        schoolId: schoolIdFinal,
        grade: formData.grade,
        role: formData.role,
      });
      const profileData = result?.data?.profile || {
        email: user.email,
        displayName: trimmedName || user.email,
        state: formData.state,
        district: districtNameFinal,
        districtId: districtIdFinal,
        school: schoolNameFinal,
        schoolId: schoolIdFinal,
        grade: formData.grade,
        role: formData.role,
      };

      if (trimmedName && trimmedName !== user.displayName) {
        await updateProfile(user, { displayName: trimmedName });
        const classesQuery = query(collection(db, 'class'), where('teacherId', '==', user.uid));
        const classesSnapshot = await getDocs(classesQuery);
        // Clever-synced classes are skipped (v3.64.0): homeroomTeacher on
        // those comes from the district roster — the write is rules-denied,
        // and one denial here used to fail the whole onboarding save.
        await Promise.all(classesSnapshot.docs
          .filter(classDoc => !isCleverRosterDoc(classDoc.data()))
          .map(classDoc =>
            updateDoc(doc(db, 'class', classDoc.id), { homeroomTeacher: trimmedName })
          ));
      }

      onComplete(profileData);
    } catch (err) {
      console.error("Error saving profile data:", err);
      // v3.84.8 guard: the profile already exists — this setup screen reached
      // an existing teacher by mistake (a failed profile read). Reload so the
      // profile loads and the app resumes; the button stays disabled, so no
      // spinner loop and no second attempt.
      if (err?.code === 'functions/failed-precondition') {
        setError('Your profile already exists — reloading…');
        setTimeout(() => window.location.reload(), 1200);
        return;
      }
      setError('Failed to save profile data. Please try again or contact support.');
      setIsSubmitting(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.9)', zIndex: 9999,
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: '20px', backdropFilter: 'blur(4px)'
  };

  const modalStyle = {
    backgroundColor: 'white', borderRadius: '16px', width: '100%',
    maxWidth: '550px', padding: '40px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    display: 'flex', flexDirection: 'column'
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: '8px',
    border: '1px solid #cbd5e1', fontSize: '1rem',
    marginBottom: '20px', boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#334155'
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, position: 'relative' }}>
        {isEditMode && (
          <button onClick={onClose} style={{ position: 'absolute', top: '15px', right: '20px', background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
        )}

        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          {!isEditMode && (
            <div style={{ background: '#007bff', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '1.5rem', display: 'inline-block', fontWeight: 'bold', marginBottom: '15px' }}>
              Pre-K
            </div>
          )}
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.8rem' }}>
            {isEditMode ? 'Update Teaching Profile' : 'Welcome to Your Dashboard'}
          </h2>
          <p style={{ color: '#64748b', marginTop: '10px', fontSize: '1rem', lineHeight: '1.5' }}>
            {isEditMode
              ? 'Update your demographic details to ensure assessment reporting is mapped to the correct district.'
              : 'To help us customize your assessment tools and ensure compliance, please let us know where you are teaching.'}
          </p>
        </div>

        {registryError && (
          <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center', fontWeight: 'bold' }}>
            {registryError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Identity */}
          <label style={labelStyle}>Provider Display Name</label>
          <input type="text" name="displayName" value={formData.displayName} onChange={handleChange} placeholder="e.g., Ms. Frizzle" style={inputStyle} />

          {/* Location */}
          <label style={labelStyle}>State</label>
          <select name="state" value={formData.state} onChange={handleChange} style={inputStyle}>
            <option value="">Select State...</option>
            {STATES.map(state => <option key={state} value={state}>{state}</option>)}
          </select>

          <label style={labelStyle}>District / Organization</label>
          {districtMode === 'dropdown' ? (
            <>
              <select
                value={formData.districtId || ''}
                onChange={handleDistrictSelect}
                style={inputStyle}
                disabled={loadingDistricts}
              >
                <option value="">{loadingDistricts ? 'Loading districts...' : 'Select your district...'}</option>
                {availableDistricts.map(d => (
                  <option key={d.id} value={d.id}>{d.name}{d.state ? ` (${d.state})` : ''}</option>
                ))}
                {!loadingDistricts && (
                  <option value={NOT_LISTED_VALUE}>--- My district isn't listed ---</option>
                )}
              </select>
              {/* Pre-fill hint when editing and district had no ID */}
              {isEditMode && formData.districtName && !formData.districtId && (
                <p style={{ fontSize: '0.8em', color: '#f59e0b', margin: '-14px 0 14px 0' }}>
                  Previously saved as "{formData.districtName}" — please re-select from the list or choose "not listed."
                </p>
              )}
            </>
          ) : (
            <>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px', marginBottom: '8px', fontSize: '0.85em', color: '#78350f', lineHeight: '1.5' }}>
                No problem! Enter your district name below. A system administrator will review it and add it
                to the official registry. You can continue using the app right away.
              </div>
              <input
                type="text"
                value={customDistrictName}
                onChange={(e) => setCustomDistrictName(e.target.value)}
                placeholder="e.g., Madison Metropolitan School District"
                style={inputStyle}
                autoFocus
              />
              <button
                type="button"
                onClick={() => { setDistrictMode('dropdown'); setCustomDistrictName(''); setFormData(prev => ({ ...prev, districtId: '', districtName: '' })); }}
                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85em', marginTop: '-14px', marginBottom: '16px', padding: 0 }}
              >
                ← Back to district list
              </button>
            </>
          )}

          <label style={labelStyle}>School / Site Name</label>
          {schoolMode === 'dropdown' ? (
            <>
              <select
                value={formData.schoolId || ''}
                onChange={handleSchoolSelect}
                style={inputStyle}
                disabled={loadingSchools || !formData.districtId}
              >
                <option value="">
                  {!formData.districtId
                    ? 'Pick a district first…'
                    : (loadingSchools ? 'Loading schools…' : 'Select your school…')}
                </option>
                {availableSchools
                  .filter(s => s.districtId === formData.districtId)
                  .map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                {!loadingSchools && formData.districtId && (
                  <option value={NOT_LISTED_VALUE}>--- My school isn't listed ---</option>
                )}
              </select>
              {/* Pre-fill hint when editing and school had no ID */}
              {isEditMode && formData.school && !formData.schoolId && formData.districtId && (
                <p style={{ fontSize: '0.8em', color: '#f59e0b', margin: '-14px 0 14px 0' }}>
                  Previously saved as "{formData.school}" — please re-select from the list or choose "not listed."
                </p>
              )}
            </>
          ) : (
            <>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px', marginBottom: '8px', fontSize: '0.85em', color: '#78350f', lineHeight: '1.5' }}>
                No problem! Enter your school name below. A system administrator will review it and add it to the registry. You can keep using the app right away.
              </div>
              <input
                type="text"
                value={customSchoolName}
                onChange={(e) => setCustomSchoolName(e.target.value)}
                placeholder="e.g., Lincoln Elementary"
                style={inputStyle}
                autoFocus
              />
              <button
                type="button"
                onClick={() => { setSchoolMode('dropdown'); setCustomSchoolName(''); setFormData(prev => ({ ...prev, schoolId: '', school: '' })); }}
                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85em', marginTop: '-14px', marginBottom: '16px', padding: 0 }}
              >
                ← Back to school list
              </button>
            </>
          )}

          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Grade Level</label>
              <select name="grade" value={formData.grade} onChange={handleChange} style={inputStyle}>
                <option value="">Select...</option>
                {gradeOptionsShown.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Your Role</label>
              <select name="role" value={formData.role} onChange={handleChange} style={inputStyle}>
                <option value="">Select...</option>
                {roleOptionsShown.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%', padding: '16px',
              backgroundColor: isSubmitting ? '#94a3b8' : '#2563eb',
              color: 'white', border: 'none', borderRadius: '8px',
              fontSize: '1.1rem', fontWeight: 'bold',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginTop: '10px'
            }}
          >
            {isSubmitting ? 'Saving Profile...' : (isEditMode ? 'Save Changes' : 'Complete Setup & Continue')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OnboardingModal;
